import { determineDeclension as declension, emptyArr } from '@beef/utils';

import { MAX_UNITS_LENGTH, TITLE_TETHERING, UNIT_LOCALE } from 'constants/accumulators';
import { AccumItemType, AccumUnit, EnrichedAccumItemType } from 'types/accumulators';

const hasDefined = (value?: number): value is number => {
  return typeof value === 'number' && !Number.isNaN(value);
};

const roundToOneDecimal = (n: number) => {
  return Math.round((n / MAX_UNITS_LENGTH.MEGABYTE) * 10) / 10;
};

export const getInternetValueAndUnit = (value: number) => {
  if (value < MAX_UNITS_LENGTH.GIGABYTE) {
    return {
      rest: (value / MAX_UNITS_LENGTH.MEGABYTE).toFixed(1),
      unit: UNIT_LOCALE.GIGABYTE,
    };
  }
  return {
    rest: (value / MAX_UNITS_LENGTH.GIGABYTE).toFixed(1),
    unit: UNIT_LOCALE.TERABYTE,
  };
};

const formatDataAsTb = ({
  rest,
  data,
  size,
}: {
  data: Partial<EnrichedAccumItemType>;
  rest?: number;
  size?: number;
}) => {
  const updateFields: Partial<EnrichedAccumItemType> = {};
  data.displayUnit = UNIT_LOCALE.TERABYTE;
  const hasSize = hasDefined(size);
  const hasRest = hasDefined(rest);

  if (hasRest) {
    const newRest = Number(getInternetValueAndUnit(rest).rest);
    data.rest = newRest;
    data.displayUnit = newRest > 0 ? getInternetValueAndUnit(rest).unit : '';
  }
  if (hasSize) {
    data.size = undefined;
    if (hasRest && rest === size) {
      data.sizeText = 'доп пакет';
    } else {
      data.sizeText = `из ${Number((size / MAX_UNITS_LENGTH.GIGABYTE).toFixed(2))} ${UNIT_LOCALE.TERABYTE} доп`;
    }
  }

  return { ...data, ...updateFields };
};

const formatDataAsGb = ({
  rest,
  data,
  size,
  baseRest,
}: {
  baseRest?: number;
  data: Partial<EnrichedAccumItemType>;
  rest?: number;
  size?: number;
}) => {
  const updateFields: Partial<EnrichedAccumItemType> = {};

  data.displayUnit = UNIT_LOCALE.GIGABYTE;

  if (hasDefined(rest)) {
    data.rest = roundToOneDecimal(rest);
  }
  if (hasDefined(size)) {
    data.size = roundToOneDecimal(size);
  }
  if (hasDefined(baseRest)) {
    data.baseRest = roundToOneDecimal(baseRest);
  }

  return { ...data, ...updateFields };
};

export const formatInternetAccumulator = (
  rest: number,
  size?: number,
  baseRest?: number,
  isGiftBucket?: boolean,
) => {
  const data: Partial<EnrichedAccumItemType> = { rest };

  if (isGiftBucket) {
    return formatDataAsTb({ rest, size, data });
  }

  return formatDataAsGb({ rest, size, data, baseRest });
};

export const formatTimeAccumulator = (rest: number, size?: number, baseRest?: number) => {
  const data: Partial<EnrichedAccumItemType> = {
    rest,
  };

  if (rest || rest === 0) {
    data.rest = Math.floor(rest / 60);
    data.displayUnit = declension(['минута', 'минуты', 'минут'], data.rest);
  }

  if (size && hasDefined(size)) {
    data.size = Math.floor(size / 60);
    data.rest = Math.floor(rest / 60);
    data.displayUnit = declension(['минута', 'минуты', 'минут'], data.size);
  }

  if (hasDefined(baseRest) && rest === 0) {
    data.baseRest = baseRest;
    data.displayUnit = 'минута';
  }

  return data;
};

const normalizeUnits = ({ unit, rest, size, baseRest, isGiftBucket }: EnrichedAccumItemType) => {
  switch (unit) {
    case AccumUnit.KBYTE: {
      return formatInternetAccumulator(rest, size, baseRest, isGiftBucket);
    }
    case AccumUnit.SECONDS: {
      return formatTimeAccumulator(rest, size, baseRest);
    }
    case AccumUnit.SMS: {
      return { displayUnit: 'смс' };
    }
    case AccumUnit.MMS: {
      return { displayUnit: 'ммс' };
    }
    case AccumUnit['SMS/MMS']: {
      return { displayUnit: 'смс и ммс' };
    }
    default: {
      return {
        displayUnit: unit,
        rest,
      };
    }
  }
};

const injectTetheringData = ({
  currentItem,
  tetheringItem,
}: {
  currentItem: AccumItemType;
  tetheringItem?: AccumItemType;
}): EnrichedAccumItemType => {
  if (currentItem.unit !== 'KBYTE' || !currentItem.isUnlimited || !tetheringItem) {
    return currentItem;
  }

  return {
    ...currentItem,
    tetheringRest: tetheringItem.rest,
    tetheringSize: tetheringItem.size,
    isLowTethering: tetheringItem.isLow,
  };
};

export const convertAccumulators = (acc?: AccumItemType[]): EnrichedAccumItemType[] => {
  if (!acc || !acc.length) return emptyArr;

  const accumulatorTethering = acc.find((a) => a.title === TITLE_TETHERING);

  return acc.map((item) => ({
    ...injectTetheringData({
      currentItem: item,
      tetheringItem: accumulatorTethering,
    }),
    ...normalizeUnits(item),
  }));
};

export const filterAccumulatorTethering = (accumulators: AccumItemType[]) => {
  return accumulators.filter((accumulator) => accumulator.title !== TITLE_TETHERING);
};
